import React, { useState, useEffect } from 'react';
import './Contact.css';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [errorDetails, setErrorDetails] = useState('');

  // Initialize EmailJS with public key if configured
  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey) {
      try {
        emailjs.init(publicKey);
      } catch (err) {
        console.error('EmailJS init error:', err);
      }
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('contact');
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorDetails('');

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setIsSubmitting(false);
      setSubmitStatus('config_error');
      setErrorDetails('Missing EmailJS configuration. Please ensure Service ID, Template ID, and Public Key are set.');
      return;
    }

    try {
      await emailjs.send(serviceId, templateId, {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        company: formData.company,
        subject: formData.subject,
        message: formData.message,
        page_url: window.location.href,
        // Helpful extras
        reply_to: formData.email,
        to_email: 'purelifeimpex@gmail.com',
        to_name: 'Purelife impex'
      }, publicKey);

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('EmailJS send error:', error);
      setSubmitStatus('error');
      const detail = (error && (error.text || error.message)) || 'Unknown error occurred while sending the email.';
      setErrorDetails(detail);
    } finally {
      setIsSubmitting(false);
      if (submitStatus !== 'config_error') {
        setTimeout(() => setSubmitStatus(''), 5000);
      }
    }
  };

  const contactInfo = [
    {
      icon: '⏰',
      title: 'Business Hours',
      details: ['Available 24/7'],
      variant: 'business-hours'
    },
    {
      icon: '📞',
      title: 'WhatsApp',
      details: ['+92 3108648888'],
      variant: 'gradient'
    },
    {
      icon: '✉️',
      title: 'Email Us',
      details: ['purelifeimpex@gmail.com', 'sales@purelifeimpex.com'],
      variant: 'gradient'
    },
    {
      icon: '🌐',
      title: 'Follow Us',
      details: ['LinkedIn: /purelifeimpex', 'Twitter: @purelifeimpex', 'Facebook: /purelifeimpex'],
      variant: 'gradient'
    }
  ];

  return (
    <section id="contact" className="contact section">
      <div className="container">
        <div className={`section-header ${isVisible ? 'animate-in' : ''}`}>
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-description">
            Ready to discuss your surgical instrument needs? Contact our expert team for personalized solutions.
            For sales, email us at <a href="mailto:sales@purelifeimpex.com">sales@purelifeimpex.com</a>.
          </p>
        </div>

        <div className="contact-content">
          <div className={`contact-info ${isVisible ? 'animate-in' : ''}`}>
            <div className="info-header">
              <h3>Contact Information</h3>
              <p>We're here to help you find the perfect surgical instruments for your medical practice.</p>
            </div>
            
            <div className="info-grid">
              {contactInfo.map((info, index) => (
                <div 
                  key={index} 
                  className={`info-card ${info.variant ? info.variant : ''}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="info-icon">{info.icon}</div>
                  <div className="info-content">
                    <h4>{info.title}</h4>
                    {info.details.map((detail, idx) => (
                      <p key={idx}>
                        {info.title === 'Email Us' || /\S+@\S+\.\S+/.test(detail) ? (
                          <a href={`mailto:${detail}`} className="email-link">{detail}</a>
                        ) : info.title === 'Call Us' || /^\+?\d[\d\s-]+$/.test(detail) ? (
                          <>
                            <a href={`tel:${detail.replace(/\s+/g, '')}`} className="phone-link">{detail}</a>
                            <a href={`https://wa.me/${detail.replace(/\D/g, '')}`} className="whatsapp-link" target="_blank" rel="noopener noreferrer" aria-label={`WhatsApp ${detail}`}>WhatsApp</a>
                          </>
                        ) : detail}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="office-map">
               <h4>Visit Our Office</h4>
               <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                 <iframe
                   title="Office Location Map"
                   src="https://maps.google.com/maps?q=32.484973,74.530276&z=14&output=embed"
                   style={{ width: '100%', height: 350, border: 0 }}
                   allowFullScreen=""
                   loading="lazy"
                   referrerPolicy="no-referrer-when-downgrade"
                 ></iframe>
               </div>
               <a
                 className="open-maps-btn"
                 href="https://maps.app.goo.gl/SHjAKdphKLmHGcxD8"
                 target="_blank"
                 rel="noopener noreferrer"
                 aria-label="Open in Google Maps"
               >
                 Open in Google Maps
               </a>
             </div>
          </div>

          <div className={`contact-form-container ${isVisible ? 'animate-in' : ''}`}>
            <div className="form-header">
              <h3>Send Us a Message</h3>
              <p>Fill out the form below and we'll get back to you within 24 hours.</p>
            </div>

            {submitStatus === 'success' && (
              <div className="success-message">
                <div className="success-icon">✅</div>
                <h4>Message Sent Successfully!</h4>
                <p>Thank you for contacting us. We'll get back to you soon.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="error-message" role="alert">
                <div className="error-icon">❌</div>
                <h4>Message Failed to Send</h4>
                <p>Please try again later or contact us directly at <a href="mailto:purelifeimpex@gmail.com">purelifeimpex@gmail.com</a> or <a href="mailto:sales@purelifeimpex.com">sales@purelifeimpex.com</a>.</p>
                {errorDetails && (
                  <p className="error-details">Error details: {errorDetails}</p>
                )}
              </div>
            )}

            {submitStatus === 'config_error' && (
              <div className="error-message" role="alert">
                <div className="error-icon">⚙️</div>
                <h4>Email Service Not Configured</h4>
                <p>The email service is not configured. Please set REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID, and REACT_APP_EMAILJS_PUBLIC_KEY.</p>
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="company">Company/Organization</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Enter your company name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="product-inquiry">Product Inquiry</option>
                  <option value="quote-request">Quote Request</option>
                  <option value="technical-support">Technical Support</option>
                  <option value="partnership">Partnership Opportunity</option>
                  <option value="general">General Question</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="6"
                  placeholder="Tell us about your requirements or questions..."
                ></textarea>
              </div>

              <button 
                type="submit" 
                className={`btn btn-primary btn-large submit-btn ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Sending Message...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;